'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Award, Book, Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import axiosInstance from '../../../utils/axiosInstance';

interface Roadmap {
  _id: string;
  roadmap_name: string;
  slug: string;
  roadmap_description: string;
}

interface RoadmapConcept {
  _id: string;
  concept_name: string;
  slug: string;
  concept_description: string;
  concept_details?: string;
  order?: number;
}

interface RoadmapClientProps {
  roadmap: Roadmap;
  concepts: RoadmapConcept[];
}

function normalizeRoadmapHtml(html: string) {
  return html.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ');
}

export default function RoadmapClient({ roadmap, concepts }: RoadmapClientProps) {
  const [markedConcepts, setMarkedConcepts] = useState<string[]>([]);
  const [selectedConcept, setSelectedConcept] = useState<RoadmapConcept | null>(null);
  const [conceptDetails, setConceptDetails] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      concepts
        .filter(concept => concept.concept_details)
        .map(concept => [concept._id, concept.concept_details as string])
    )
  );
  const conceptDetailsRef = useRef(conceptDetails);
  const [loadingConceptId, setLoadingConceptId] = useState('');
  const [conceptErrors, setConceptErrors] = useState<Record<string, string>>({});
  const conceptRequestRef = useRef<AbortController | null>(null);
  const storageKey = `roadmap-progress-${roadmap._id}`;

  const loadConceptDetails = useCallback(async (concept: RoadmapConcept) => {
    if (concept.concept_details || conceptDetailsRef.current[concept._id]) return;

    conceptRequestRef.current?.abort();
    const controller = new AbortController();
    conceptRequestRef.current = controller;

    try {
      setLoadingConceptId(concept._id);
      setConceptErrors(prev => ({ ...prev, [concept._id]: '' }));
      const response = await axiosInstance.get(
        `/api/roadmaps/slug/${encodeURIComponent(roadmap.slug)}/concept/${encodeURIComponent(concept.slug)}`,
        { signal: controller.signal, timeout: 15000 }
      );
      const details = response.data?.concept?.concept_details;

      if (typeof details !== 'string') throw new Error('Concept content is unavailable');
      conceptDetailsRef.current = { ...conceptDetailsRef.current, [concept._id]: details };
      setConceptDetails(prev => ({ ...prev, [concept._id]: details }));
    } catch {
      if (!controller.signal.aborted) {
        setConceptErrors(prev => ({ ...prev, [concept._id]: 'Failed to load this concept. Please try again.' }));
      }
    } finally {
      if (conceptRequestRef.current === controller) conceptRequestRef.current = null;
      setLoadingConceptId(current => current === concept._id ? '' : current);
    }
  }, [roadmap.slug]);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      try {
        const storedConcepts = JSON.parse(localStorage.getItem(storageKey) || '[]');
        if (Array.isArray(storedConcepts)) setMarkedConcepts(storedConcepts);
      } catch {
        localStorage.setItem(storageKey, JSON.stringify([]));
      }
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [storageKey]);

  useEffect(() => {
    if (selectedConcept) void loadConceptDetails(selectedConcept);
  }, [loadConceptDetails, selectedConcept]);

  useEffect(() => () => conceptRequestRef.current?.abort(), []);

  const toggleConceptMark = (conceptId: string) => {
    try {
      setMarkedConcepts(prev => {
        const next = prev.includes(conceptId)
          ? prev.filter(id => id !== conceptId)
          : [...prev, conceptId];

        localStorage.setItem(storageKey, JSON.stringify(next));
        return next;
      });
    } catch {
      setMarkedConcepts(prev => prev);
    }
  };

  const openConcept = (concept: RoadmapConcept) => {
    setSelectedConcept(concept);
  };

  const closeConcept = () => {
    conceptRequestRef.current?.abort();
    setSelectedConcept(null);
  };

  const selectedMarked = selectedConcept ? markedConcepts.includes(selectedConcept._id) : false;

  return (
    <section className="study-section-soft relative pt-4 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {concepts.length > 0 ? (
          <div className="relative p-2 sm:p-6 scroll-mt-32">
            <div className="flex items-center mb-8">
              <Award className="study-link mr-2" size={24} aria-hidden="true" />
              <h2 className="study-title text-2xl sm:text-3xl font-black">Learning Path</h2>
            </div>

            <div className="relative w-full max-w-4xl mx-auto pb-16">
              <div className="study-roadmap-track absolute left-6 md:left-1/2 md:-translate-x-1/2 w-1 sm:w-2 h-full rounded-full"></div>

              {concepts.map((concept, index) => (
                <ConceptCard
                  key={concept._id}
                  concept={concept}
                  index={index}
                  totalConcepts={concepts.length}
                  marked={markedConcepts.includes(concept._id)}
                  onClick={() => openConcept(concept)}
                  onMarkToggle={() => toggleConceptMark(concept._id)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="study-card rounded-2xl p-8 text-center">
            <Book className="study-muted w-12 h-12 mx-auto mb-3" aria-hidden="true" />
            <p className="study-muted">This roadmap is currently under development.</p>
          </div>
        )}
      </div>

      <ConceptPopup
        key={selectedConcept?._id ?? 'closed'}
        concept={selectedConcept}
        conceptDetails={selectedConcept
          ? selectedConcept.concept_details || conceptDetails[selectedConcept._id] || ''
          : ''}
        loading={Boolean(selectedConcept && loadingConceptId === selectedConcept._id)}
        error={selectedConcept ? conceptErrors[selectedConcept._id] || '' : ''}
        onRetry={() => selectedConcept && loadConceptDetails(selectedConcept)}
        marked={selectedMarked}
        onClose={closeConcept}
        onMarkToggle={() => selectedConcept && toggleConceptMark(selectedConcept._id)}
      />
    </section>
  );
}

function ConceptCard({
  concept,
  index,
  totalConcepts,
  marked,
  onClick,
  onMarkToggle
}: {
  concept: RoadmapConcept;
  index: number;
  totalConcepts: number;
  marked: boolean;
  onClick: () => void;
  onMarkToggle: () => void;
}) {
  const isEven = index % 2 === 0;
  const isLast = index === totalConcepts - 1;

  return (
    <div className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} md:items-center mb-12 md:mb-16 relative pl-16 md:pl-0`}>
      <div className="w-full md:w-5/12 z-10">
        <button
          type="button"
          onClick={onClick}
          className={`study-resource-card w-full text-left p-4 rounded-xl cursor-pointer transform transition-[color,background-color,border-color,opacity,transform] duration-300 relative overflow-hidden ${marked ? 'study-roadmap-card-complete' : ''}`}
        >
          <h3 className="study-title text-xl font-bold mb-3 pr-8">
            {concept.concept_name}
          </h3>

          {marked && (
            <div className="absolute top-0 right-0 bg-[var(--study-primary)] text-white p-2 rounded-bl-lg">
              <Check size={16} aria-hidden="true" />
            </div>
          )}

          <p className="study-muted text-sm md:text-base">
            {concept.concept_description?.substring(0, 120)}
            {concept.concept_description?.length > 120 ? '...' : ''}
          </p>

          <div className="mt-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-0 text-sm">
            <span className="study-link font-medium">Learn more</span>
            <span className="study-muted">{marked ? 'Completed' : 'Not started'}</span>
          </div>
        </button>
      </div>

      <div className="absolute left-0 top-0 md:relative md:w-2/12 md:flex md:justify-center">
        <button
          type="button"
          className={`w-12 h-12 rounded-full ${marked ? 'study-roadmap-node-complete' : 'study-roadmap-node'} shadow-lg z-20 flex items-center justify-center cursor-pointer transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-110 focus:outline-none`}
          onClick={onMarkToggle}
          aria-label={marked ? 'Mark as incomplete' : 'Mark as complete'}
        >
          {marked ? (
            <Check className="text-white" size={20} aria-hidden="true" />
          ) : (
            <span className="font-bold">{index + 1}</span>
          )}
        </button>

        {!isLast && (
          <div
            className="study-roadmap-connector absolute top-12 left-6 md:left-1/2 md:-translate-x-1/2 w-1 h-16"
            style={{ zIndex: -1 }}
          ></div>
        )}
      </div>

      <div className="hidden md:block md:w-5/12"></div>
    </div>
  );
}

function ConceptPopup({
  concept,
  conceptDetails,
  loading,
  error,
  onRetry,
  marked,
  onClose,
  onMarkToggle
}: {
  concept: RoadmapConcept | null;
  conceptDetails: string;
  loading: boolean;
  error: string;
  onRetry: () => void;
  marked: boolean;
  onClose: () => void;
  onMarkToggle: () => void;
}) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const quote = useMemo(() => {
    const quotes = [
      'Focus on the step in front of you, not the whole staircase.',
      'Every big journey begins with a single clear step.',
      'Consistency beats intensity when the path is long.',
      'You grow stronger every time you refuse to quit.',
      'A plan followed becomes reality.'
    ];
    if (!concept) return '';

    const quoteIndex = concept._id
      .split('')
      .reduce((total, character) => total + character.charCodeAt(0), 0) % quotes.length;

    return quotes[quoteIndex];
  }, [concept]);

  useEffect(() => {
    if (!concept) return;

    const scrollY = window.scrollY;
    const previousStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow
    };

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.position = previousStyles.position;
      document.body.style.top = previousStyles.top;
      document.body.style.left = previousStyles.left;
      document.body.style.right = previousStyles.right;
      document.body.style.width = previousStyles.width;
      document.body.style.overflow = previousStyles.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [concept]);

  useEffect(() => {
    if (!concept || !conceptDetails || !contentRef.current) return;

    const cleanupCallbacks = Array.from(
      contentRef.current.querySelectorAll<HTMLElement>('pre, .ql-code-block-container')
    ).map(codeBlock => {
      const shell = document.createElement('div');
      const scrollArea = document.createElement('div');
      const copyButton = document.createElement('button');
      let resetTimer: number | undefined;

      shell.className = 'roadmap-code-shell';
      scrollArea.className = 'roadmap-code-scroll';
      copyButton.type = 'button';
      copyButton.className = 'roadmap-code-copy';
      copyButton.textContent = 'Copy';
      copyButton.setAttribute('aria-label', 'Copy code');

      const handleCopy = async () => {
        try {
          await navigator.clipboard.writeText(codeBlock.innerText);
          copyButton.textContent = 'Copied';
          window.clearTimeout(resetTimer);
          resetTimer = window.setTimeout(() => {
            copyButton.textContent = 'Copy';
          }, 1500);
        } catch {
          copyButton.textContent = 'Copy failed';
        }
      };

      copyButton.addEventListener('click', handleCopy);
      codeBlock.parentNode?.insertBefore(shell, codeBlock);
      shell.append(copyButton, scrollArea);
      scrollArea.append(codeBlock);

      return () => {
        window.clearTimeout(resetTimer);
        copyButton.removeEventListener('click', handleCopy);
        if (shell.parentNode) {
          shell.parentNode.insertBefore(codeBlock, shell);
          shell.remove();
        }
      };
    });

    return () => cleanupCallbacks.forEach(cleanup => cleanup());
  }, [concept, conceptDetails]);

  if (!concept) return null;

  return (
    <div className="fixed inset-0 bg-black/35 backdrop-blur-sm flex items-center justify-center z-50 px-4 py-6">
      <div className="study-roadmap-modal rounded-2xl w-full max-w-4xl max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-4">
          <h2 className="study-title text-xl sm:text-2xl lg:text-3xl font-bold leading-tight pr-4">
            {concept.concept_name}
          </h2>
          <button
            type="button"
            className="study-link flex-shrink-0 p-2 rounded-full hover:bg-[var(--study-primary-soft)] transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="mx-6 sm:mx-8 h-px bg-[var(--study-border)]"></div>

        <div className="px-6 sm:px-8 py-6 overflow-y-auto overflow-x-hidden flex-grow min-w-0">
          {loading && !conceptDetails ? (
            <div className="flex min-h-48 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--study-border)] border-t-[var(--study-primary)]" aria-label="Loading concept" />
            </div>
          ) : error && !conceptDetails ? (
            <div className="study-card rounded-xl p-6 text-center">
              <p className="study-muted mb-4">{error}</p>
              <button type="button" onClick={onRetry} className="study-button-primary rounded-lg px-4 py-2 text-sm font-semibold">
                Try Again
              </button>
            </div>
          ) : (
            <div
              ref={contentRef}
              className="roadmap-prose"
              dangerouslySetInnerHTML={{ __html: normalizeRoadmapHtml(conceptDetails) }}
            />
          )}
        </div>

        <div className="border-t border-[var(--study-border)]">
          <button
            type="button"
            onClick={() => setActionsOpen(open => !open)}
            className="study-link mx-auto flex items-center gap-2 px-4 py-3 text-sm font-semibold hover:text-[var(--study-primary-hover)] transition-colors"
            aria-expanded={actionsOpen}
          >
            {actionsOpen ? (
              <>
                <ChevronUp size={18} aria-hidden="true" />
                Hide actions
              </>
            ) : (
              <>
                <ChevronDown size={18} aria-hidden="true" />
                Show actions
              </>
            )}
          </button>

          <div className={`overflow-hidden transition-[color,background-color,border-color,opacity,transform] duration-300 ease-in-out ${actionsOpen ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'}`}>
            {quote && (
              <div className="px-6 sm:px-8 py-4">
                <div className="study-muted text-xs sm:text-sm italic font-medium leading-relaxed pl-4 border-l border-[rgba(15,110,86,0.25)]">
                  &quot;{quote}&quot;
                </div>
              </div>
            )}

            <div className="px-6 sm:px-8 pb-6 flex items-center justify-center">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  type="button"
                  className={`inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-xl transition-colors ${marked ? 'bg-[var(--study-primary-soft)] hover:bg-[var(--study-primary-soft)] text-[var(--study-primary)] border border-[rgba(15,110,86,0.25)]' : 'study-button-primary'}`}
                  onClick={onMarkToggle}
                >
                  <Check size={16} className="mr-2" aria-hidden="true" />
                  {marked ? 'Completed' : 'Mark Complete'}
                </button>

                <button
                  type="button"
                  className="study-button-primary inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-xl transition-colors"
                  onClick={onClose}
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
